import type { Organization, OrganizationMember } from '@/types';

export type AnalyticsOrgType = 'msp' | 'customer' | 'supplier' | 'unknown';
export type AnalyticsAccountRelationship = 'msp_customer' | 'direct_customer' | 'supplier_portal' | 'unknown';
export type AnalyticsNavigationSource =
  | 'command_palette'
  | 'navigator'
  | 'sidebar'
  | 'top_nav'
  | 'breadcrumb'
  | 'table_link'
  | 'card_link'
  | 'cta'
  | 'direct_url'
  | 'browser_back_forward'
  | 'redirect'
  | 'unknown';

export type AnalyticsNavigationSurface =
  | 'app_shell'
  | 'navigator'
  | 'supplier_profile'
  | 'customer_profile'
  | 'dashboard'
  | 'onboarding'
  | 'auth'
  | 'vendor_portal'
  | 'unknown';

export type AnalyticsContextProperties = {
  'active org id': string | null;
  'active org type': AnalyticsOrgType;
  'msp org id': string | null;
  'customer org id': string | null;
  'supplier org id': string | null;
  'supplier id': string | null;
  'user role': string | null;
  'account relationship': AnalyticsAccountRelationship;
};

export type AnalyticsRouteDescriptor = {
  pageKey: string;
  routePattern: string;
  supplierId: string | null;
};

type ResolveAnalyticsContextInput = {
  organization: Organization | null;
  membership: OrganizationMember | null;
  isMspOrg: boolean;
  isMspContext: boolean;
  mspHomeOrg: Organization | null;
  route: AnalyticsRouteDescriptor;
};

const uuidSegmentPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const routePatterns: Array<{ pattern: string; pageKey: string; regex: RegExp; supplierParamIndex?: number }> = [
  { pattern: '/', pageKey: 'home', regex: /^\/$/ },
  { pattern: '/auth', pageKey: 'auth', regex: /^\/auth$/ },
  { pattern: '/onboarding', pageKey: 'onboarding', regex: /^\/onboarding$/ },
  { pattern: '/plan-selection', pageKey: 'plan_selection', regex: /^\/plan-selection$/ },
  { pattern: '/upgrade/supplier-plus', pageKey: 'supplier_plus_upgrade', regex: /^\/upgrade\/supplier-plus$/ },
  { pattern: '/admin', pageKey: 'admin', regex: /^\/admin$/ },
  { pattern: '/admin/data-grid-harness', pageKey: 'admin_data_grid_harness', regex: /^\/admin\/data-grid-harness$/ },
  { pattern: '/admin/storybook', pageKey: 'admin_storybook', regex: /^\/admin\/storybook$/ },
  { pattern: '/admin/content/:id', pageKey: 'admin_content', regex: /^\/admin\/content\/([^/]+)$/ },
  { pattern: '/admin/advisories/:advisoryId', pageKey: 'admin_advisory_console', regex: /^\/admin\/advisories\/([^/]+)$/ },
  { pattern: '/share/questionnaires/:shareToken', pageKey: 'public_questionnaire_share', regex: /^\/share\/questionnaires\/([^/]+)$/ },
  { pattern: '/team/join', pageKey: 'team_join', regex: /^\/team\/join$/ },
  { pattern: '/dashboard', pageKey: 'dashboard_redirect', regex: /^\/dashboard$/ },
  { pattern: '/navigator', pageKey: 'navigator', regex: /^\/navigator$/ },
  { pattern: '/navigator/foundation', pageKey: 'navigator_foundation', regex: /^\/navigator\/foundation$/ },
  { pattern: '/navigator/crown-jewels', pageKey: 'navigator_crown_jewels', regex: /^\/navigator\/crown-jewels$/ },
  { pattern: '/navigator/suppliers', pageKey: 'navigator_suppliers', regex: /^\/navigator\/suppliers$/ },
  { pattern: '/navigator/contracts', pageKey: 'navigator_contracts', regex: /^\/navigator\/contracts$/ },
  { pattern: '/insights', pageKey: 'insights', regex: /^\/insights$/ },
  { pattern: '/suppliers/new', pageKey: 'supplier_new_redirect', regex: /^\/suppliers\/new$/ },
  { pattern: '/suppliers/:supplierId', pageKey: 'supplier_profile', regex: /^\/suppliers\/([^/]+)$/, supplierParamIndex: 1 },
  { pattern: '/suppliers', pageKey: 'suppliers', regex: /^\/suppliers$/ },
  { pattern: '/certifications', pageKey: 'certifications', regex: /^\/certifications$/ },
  { pattern: '/breach-feed', pageKey: 'breach_feed', regex: /^\/breach-feed$/ },
  { pattern: '/alert-centre', pageKey: 'alert_centre', regex: /^\/alert-centre$/ },
  { pattern: '/alerts', pageKey: 'alerts', regex: /^\/alerts$/ },
  { pattern: '/advisories', pageKey: 'advisories', regex: /^\/advisories$/ },
  { pattern: '/advisories/:advisoryId', pageKey: 'advisory', regex: /^\/advisories\/([^/]+)$/ },
  { pattern: '/check/:domain', pageKey: 'public_runtime_check', regex: /^\/check\/([^/]+)$/ },
  { pattern: '/settings', pageKey: 'settings', regex: /^\/settings$/ },
  { pattern: '/docs', pageKey: 'docs', regex: /^\/docs$/ },
  { pattern: '/team', pageKey: 'team', regex: /^\/team$/ },
  { pattern: '/marketplace', pageKey: 'marketplace', regex: /^\/marketplace$/ },
  { pattern: '/actions', pageKey: 'actions', regex: /^\/actions$/ },
  { pattern: '/customers', pageKey: 'customers', regex: /^\/customers$/ },
  { pattern: '/msp-insights', pageKey: 'msp_insights', regex: /^\/msp-insights$/ },
  { pattern: '/msp-heatmap', pageKey: 'msp_heatmap', regex: /^\/msp-heatmap$/ },
  { pattern: '/msp-templates', pageKey: 'msp_templates', regex: /^\/msp-templates$/ },
  { pattern: '/pitch-book', pageKey: 'pitch_book', regex: /^\/pitch-book$/ },
  { pattern: '/dependencies', pageKey: 'dependencies', regex: /^\/dependencies$/ },
  { pattern: '/reports', pageKey: 'reports', regex: /^\/reports$/ },
  { pattern: '/import', pageKey: 'import', regex: /^\/import$/ },
  { pattern: '/vendor', pageKey: 'vendor_dashboard', regex: /^\/vendor$/ },
  { pattern: '/vendor/claim/auth', pageKey: 'vendor_claim_auth', regex: /^\/vendor\/claim\/auth$/ },
  { pattern: '/vendor/claim', pageKey: 'vendor_claim', regex: /^\/vendor\/claim$/ },
  { pattern: '/vendor/profile', pageKey: 'vendor_profile', regex: /^\/vendor\/profile$/ },
  { pattern: '/vendor/certifications', pageKey: 'vendor_certifications', regex: /^\/vendor\/certifications$/ },
  { pattern: '/vendor/policies', pageKey: 'vendor_policies', regex: /^\/vendor\/policies$/ },
  { pattern: '/vendor/questionnaires/:id', pageKey: 'vendor_questionnaire_response', regex: /^\/vendor\/questionnaires\/([^/]+)$/ },
  { pattern: '/vendor/questionnaires', pageKey: 'vendor_questionnaires', regex: /^\/vendor\/questionnaires$/ },
  { pattern: '/vendor/visibility', pageKey: 'vendor_visibility', regex: /^\/vendor\/visibility$/ },
  { pattern: '/vendor/actions', pageKey: 'vendor_actions', regex: /^\/vendor\/actions$/ },
  { pattern: '/vendor/advisories', pageKey: 'vendor_advisories', regex: /^\/vendor\/advisories$/ },
  { pattern: '/vendor/advisories/:advisoryId', pageKey: 'vendor_advisory', regex: /^\/vendor\/advisories\/([^/]+)$/ },
  { pattern: '/vendor/team/join', pageKey: 'vendor_team_join', regex: /^\/vendor\/team\/join$/ },
  { pattern: '/vendor/team', pageKey: 'vendor_team', regex: /^\/vendor\/team$/ },
  { pattern: '/vendor/settings', pageKey: 'vendor_settings', regex: /^\/vendor\/settings$/ },
  { pattern: '/__sentry-test', pageKey: 'sentry_test', regex: /^\/__sentry-test$/ },
];

function normalizePathname(pathname: string) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.replace(/\/+$/, '') || '/';
}

function safeDecodeUriSegment(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function getAnalyticsRouteDescriptor(pathname: string): AnalyticsRouteDescriptor {
  const normalizedPathname = normalizePathname(pathname);

  for (const routePattern of routePatterns) {
    const match = routePattern.regex.exec(normalizedPathname);
    if (match) {
      return {
        pageKey: routePattern.pageKey,
        routePattern: routePattern.pattern,
        supplierId: routePattern.supplierParamIndex ? safeDecodeUriSegment(match[routePattern.supplierParamIndex]) : null,
      };
    }
  }

  const sanitizedSegments = normalizedPathname
    .split('/')
    .map((segment) => (uuidSegmentPattern.test(segment) ? ':id' : segment));
  const routePattern = sanitizedSegments.join('/') || '/';

  return {
    pageKey: routePattern === '/' ? 'home' : routePattern.replace(/^\/+/, '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'unknown',
    routePattern,
    supplierId: null,
  };
}

export function resolveAnalyticsContext({
  organization,
  membership,
  isMspOrg,
  isMspContext,
  mspHomeOrg,
  route,
}: ResolveAnalyticsContextInput): AnalyticsContextProperties {
  const activeOrgType: AnalyticsOrgType = route.routePattern.startsWith('/vendor')
    ? 'supplier'
    : isMspOrg && !isMspContext
      ? 'msp'
      : organization
        ? 'customer'
        : 'unknown';

  const mspOrgId = isMspOrg && !isMspContext
    ? organization?.id ?? null
    : isMspContext
      ? mspHomeOrg?.id ?? null
      : null;
  const customerOrgId = activeOrgType === 'customer' ? organization?.id ?? null : null;
  const supplierOrgId = activeOrgType === 'supplier' ? organization?.id ?? null : null;
  const accountRelationship: AnalyticsAccountRelationship = activeOrgType === 'supplier'
    ? 'supplier_portal'
    : mspOrgId && customerOrgId
      ? 'msp_customer'
      : customerOrgId
        ? 'direct_customer'
        : 'unknown';

  return {
    'active org id': organization?.id ?? null,
    'active org type': activeOrgType,
    'msp org id': mspOrgId,
    'customer org id': customerOrgId,
    'supplier org id': supplierOrgId,
    'supplier id': route.supplierId,
    'user role': membership?.role ?? null,
    'account relationship': accountRelationship,
  };
}
